import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { auth } from '../config/firebase'; // FIX: Import Firebase auth instead of Supabase

// The user might be using an older version of axios or a polyfill where this is valid.
// For modern axios, the signal is part of the config object directly.
// We'll leave the import commented out unless it causes an issue, as the usage is correct.
// import AbortSignal from 'axios'; 

// Extend the config interface to include metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: { startTime: number };
}

const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 1200000, // 20 minutes
});

// Debug logging
console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);

// Request interceptor for auth and logging
apiClient.interceptors.request.use(
  async (config: ExtendedAxiosRequestConfig) => {
    // --- FIX: Replaced Supabase logic with Firebase logic ---
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Could not get Firebase ID token:", error);
        // The request will proceed without an auth header.
        // Depending on the endpoint, this may be desired or may result in a 401 error.
      }
    }
    // --- End of Fix ---
    
    // Log API requests
    const method = config.method?.toUpperCase();
    const url = config.url;
    const timestamp = new Date().toISOString();
    
    console.log(`[${timestamp}] API Request: ${method} ${url}`);
    
    // Log file upload details if it's a FormData request
    if (config.data instanceof FormData) {
      console.log('FormData contents:');
      // Use Array.from to convert iterator to array for TypeScript compatibility
      const entries = Array.from(config.data.entries());
      entries.forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(`  ${key}: File "${value.name}" (${(value.size / 1024 / 1024).toFixed(2)} MB)`);
        } else {
          console.log(`  ${key}: ${typeof value === 'string' ? value.substring(0, 100) + '...' : value}`);
        }
      });
    }
    
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging
apiClient.interceptors.response.use(
  (response: AxiosResponse & { config: ExtendedAxiosRequestConfig }) => {
    const duration = Date.now() - (response.config.metadata?.startTime || 0);
    const method = response.config.method?.toUpperCase();
    const url = response.config.url;
    const status = response.status;
    const timestamp = new Date().toISOString();
    
    console.log(`[${timestamp}] API Response: ${method} ${url} - ${status} (${duration}ms)`);
    
    // Log response size for file uploads
    if (response.data && typeof response.data === 'object') {
      const responseSize = JSON.stringify(response.data).length;
      if (responseSize > 1000) {
        console.log(`Response size: ${(responseSize / 1024).toFixed(2)} KB`);
      }
    }
    
    return response;
  },
  (error: any) => {
    const duration = Date.now() - (error.config?.metadata?.startTime || 0);
    const method = error.config?.method?.toUpperCase();
    const url = error.config?.url;
    const status = error.response?.status;
    const timestamp = new Date().toISOString();
    
    console.error(`[${timestamp}] API Error: ${method} ${url} - ${status || 'Network Error'} (${duration}ms)`);
    
    if (error.response?.data?.detail) {
      console.error('Error details:', error.response.data.detail);
    }
    
    // Log timeout errors specifically
    if (error.code === 'ECONNABORTED') {
      console.error('Request timed out - consider increasing timeout or optimizing server processing');
    }
    
    return Promise.reject(error);
  }
);

// Define interfaces for API responses based on Pydantic models
export interface MrpData {
  eigen_dict: Record<string, number>;
  plan_dict: Record<string, number>;
  beschaffungsart_dict?: Record<string, string>; // Optional, contains E/F values
}

// Updated MRP response to include available Werks and raw MRP data
export interface MrpUploadApiResponse {
  filename: string;
  message: string;
  available_werks: string[];
  mrp_dataframe_json: string; // Raw MRP dataframe as JSON
}

// New interface for Werk selection
export interface WerkSelectionPayload {
  mrp_dataframe_json: string;
  selected_werk: string;
}

export interface WerkSelectionApiResponse {
  message: string;
  mrp_data: MrpData;
  selected_werk: string;
}

export interface FilteredItem {
  materialNumber: string;
  articleName: string;
  reason: string;
}

export interface FilteredItemsData {
  zeroTimeItems: FilteredItem[];
  notInMrpItems: FilteredItem[];
}

export interface ProcessedBomData {
  dataframeJson: string;
  rootIndex: number | null;
  filename?: string | null;
  filteredItems?: FilteredItemsData; // Make optional for backward compatibility
}

export interface BomUploadApiResponse {
  bomData: ProcessedBomData;
  message: string;
}

export interface AddRootPayload {
    name: string;
    time: number;
    currentBomDataJson: string;
    currentFilename?: string | null;
}

export interface AddRootApiResponse {
    bomData: ProcessedBomData;
    message: string;
}

export interface GanttChartApiResponse {
    error: boolean;
    message?: string;
    data?: any[]; // Plotly data structure
    layout?: any; // Plotly layout structure
}

export const uploadMrp = (file: File): Promise<{ data: MrpUploadApiResponse }> => {
  console.log(`Uploading MRP file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
  
  const formData = new FormData();
  formData.append('file', file);
  
  return apiClient.post('/upload/mrp', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Individual timeout for MRP uploads
    timeout: 1200000, // 20 minutes
  });
};

// New API call for Werk selection
export const selectWerk = (payload: WerkSelectionPayload): Promise<{ data: WerkSelectionApiResponse }> => {
  console.log(`Selecting Werk: ${payload.selected_werk}`);
  
  return apiClient.post('/mrp/select-werk', payload, {
    timeout: 30000, // 30 seconds for Werk selection
  });
};

export const uploadBom = (
  bomExcelFile: File,
  mrpData: MrpData
): Promise<{ data: BomUploadApiResponse }> => {
  console.log(`Uploading BOM file: ${bomExcelFile.name} (${(bomExcelFile.size / 1024 / 1024).toFixed(2)} MB)`);
  console.log(`MRP data: ${Object.keys(mrpData.eigen_dict).length} eigen entries, ${Object.keys(mrpData.plan_dict).length} plan entries`);
  
  const formData = new FormData();

  // 1. Append the BOM Excel file
  formData.append('bomExcelFile', bomExcelFile, bomExcelFile.name);

  // 2. Create a JSON file in memory from mrpData
  const mrpPayloadForFile = {
      eigen_dict: mrpData.eigen_dict,
      plan_dict: mrpData.plan_dict,
      beschaffungsart_dict: mrpData.beschaffungsart_dict || {},
  };
  const mrpJsonString = JSON.stringify(mrpPayloadForFile);
  const mrpJsonBlob = new Blob([mrpJsonString], { type: 'application/json' });
  formData.append('mrpDataFile', mrpJsonBlob, 'mrp_data.json');

  console.log(`MRP JSON payload size: ${(mrpJsonString.length / 1024).toFixed(2)} KB`);

  return apiClient.post('/upload/bom', formData, {
    headers: {
      // 'Content-Type': 'multipart/form-data' will be set automatically
    },
    // Extended timeout for BOM processing
    timeout: 1200000, // 20 minutes
  });
};

export const addRootToBomAPI = (payload: AddRootPayload): Promise<{data: AddRootApiResponse}> => {
  console.log(`Adding root product: "${payload.name}" with time: ${payload.time}`);
  
  return apiClient.post('/bom/add-root', payload, {
    timeout: 30000, // 30 seconds for root addition
  });
};

export const fetchGanttChartData = (
  bomDataJson: string,
  rootIndex: number,
  deadline?: number | null,
  signal?: AbortSignal // This is the standard AbortSignal, not an axios-specific one
): Promise<{ data: GanttChartApiResponse }> => {
  console.log(`Generating Gantt chart - Root index: ${rootIndex}, Deadline: ${deadline}`);
  
  // Estimate data size
  const dataSize = new Blob([bomDataJson]).size;
  console.log(`BOM data size: ${(dataSize / 1024).toFixed(2)} KB`);
  
  return apiClient.post('/chart/gantt', {
    bomDataJson,
    rootIndex,
    deadline: deadline === undefined ? null : deadline,
  }, {
    signal,
    timeout: 1200000, // 20 minutes
  });
};

export default apiClient;